"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

// Wingo UI Pro · https://wingo-ui.com/legal/license

import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import { animate as animateValue, motion, useMotionValue, useTransform, type MotionValue } from "motion/react";
import { Badge } from "@/components/wingo/ui/badge";
import { Tooltip, TooltipGroup } from "@/components/wingo/ui/tooltip";
import { useControllableState } from "@/components/wingo/hooks/use-controllable-state";
import { gestures, springs, usePrefersReducedMotion } from "@/components/wingo/lib/motion";
import { colorVars, toneClasses, type Tone } from "@/components/wingo/lib/tones";
import { useDefaults } from "@/components/wingo/lib/ui-config";
import { cn } from "@/components/wingo/lib/utils";
import { withRef } from "@/components/wingo/lib/with-ref";

export type SegmentedControlSize = "sm" | "md" | "lg";
export type SegmentedControlRadius = "none" | "sm" | "md" | "lg" | "full";
export type SegmentedControlSlot = "item" | "thumb" | "icon" | "label" | "badge" | "divider";
export type { Tone as SegmentedControlTone };

export type SegmentedControlOption = {
  value: string;
  label?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  // a number renders a small count badge (hidden at 0), any other node renders as is
  badge?: ReactNode | number;
  // shown on hover, focus and long press; icon-only items fall back to their label
  tooltip?: ReactNode;
};

// ---------------------------------------------------------------- sizes and shapes

// the track is 32 / 40 / 48px (44px md on touch); items fill it inside its 2px padding
const TRACK_SIZES: Record<SegmentedControlSize, string> = {
  sm: "h-8",
  md: "h-10 pointer-coarse:h-11",
  lg: "h-12",
};

// items reach a 44px hit area on touch: sm keeps its look and grows an invisible hit box, md covers the track padding
const ITEM_SIZES: Record<SegmentedControlSize, string> = {
  sm: "gap-1.5 px-2.5 text-[13px] pointer-coarse:after:absolute pointer-coarse:after:inset-x-0 pointer-coarse:after:-inset-y-2",
  md: "gap-2 px-3 text-sm pointer-coarse:after:absolute pointer-coarse:after:inset-x-0 pointer-coarse:after:-inset-y-0.5",
  lg: "gap-2 px-4 text-[15px]",
};

const ICON_ONLY_SIZES: Record<SegmentedControlSize, string> = {
  sm: "min-w-9 px-2.5",
  md: "min-w-11 px-3",
  lg: "min-w-13 px-3.5",
};

// svgs without their own size- class follow the size: 14 / 16 / 18px
const ICON_SIZES: Record<SegmentedControlSize, string> = {
  sm: "[&_svg:not([class*='size-'])]:size-3.5",
  md: "[&_svg:not([class*='size-'])]:size-4",
  lg: "[&_svg:not([class*='size-'])]:size-[18px]",
};

const DIVIDER_INSETS: Record<SegmentedControlSize, string> = {
  sm: "inset-y-1.5",
  md: "inset-y-2",
  lg: "inset-y-2.5",
};

// radius grows with the size so every size reads as the same shape (the button's map)
const RADII: Record<SegmentedControlRadius, Record<SegmentedControlSize, string>> = {
  none: { sm: "rounded-none", md: "rounded-none", lg: "rounded-none" },
  sm: { sm: "rounded-[6px]", md: "rounded-[8px]", lg: "rounded-[10px]" },
  md: { sm: "rounded-[8px]", md: "rounded-[10px]", lg: "rounded-[12px]" },
  lg: { sm: "rounded-[10px]", md: "rounded-[14px]", lg: "rounded-[16px]" },
  full: { sm: "rounded-full", md: "rounded-full", lg: "rounded-full" },
};

// items and the thumb sit 2px inside the track, so they are 2px rounder-in
const INNER_RADII: Record<SegmentedControlRadius, Record<SegmentedControlSize, string>> = {
  none: { sm: "rounded-none", md: "rounded-none", lg: "rounded-none" },
  sm: { sm: "rounded-[4px]", md: "rounded-[6px]", lg: "rounded-[8px]" },
  md: { sm: "rounded-[6px]", md: "rounded-[8px]", lg: "rounded-[10px]" },
  lg: { sm: "rounded-[8px]", md: "rounded-[12px]", lg: "rounded-[14px]" },
  full: { sm: "rounded-full", md: "rounded-full", lg: "rounded-full" },
};

// the thumb's radius in px, counter-scaled while it stretches between items of different widths
const THUMB_RADIUS_PX: Record<SegmentedControlRadius, Record<SegmentedControlSize, number>> = {
  none: { sm: 0, md: 0, lg: 0 },
  sm: { sm: 4, md: 6, lg: 8 },
  md: { sm: 6, md: 8, lg: 10 },
  lg: { sm: 8, md: 12, lg: 14 },
  full: { sm: 9999, md: 9999, lg: 9999 },
};

// ---------------------------------------------------------------- looks

const TRACK = "bg-foreground/5 p-0.5";

// the raised thumb shared with segmented tabs; mixed lighter in dark mode, where --popover and a 5% track over
// --surface are nearly the same gray
const THUMB_NEUTRAL = "bg-popover shadow-raised dark:bg-[color-mix(in_oklab,rgb(var(--wui-foreground))_14%,rgb(var(--wui-popover)))]";
const THUMB_TONE = "bg-[color:var(--tone)] shadow-raised";

// touch-manipulation and the transparent tap highlight live here, so a copied control never flashes gray on ios.
// hover changes the text only (ios); a press dims the label of an unselected item
const ITEM_BASE =
  "group/segment relative inline-flex h-full min-w-0 cursor-pointer touch-manipulation select-none items-center justify-center whitespace-nowrap font-medium text-muted-foreground outline-hidden [-webkit-tap-highlight-color:transparent] transition-[color,opacity] duration-fast ease-smooth data-[state=unchecked]:hover:text-foreground focus-visible:z-20 focus-visible:outline-2 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-ring data-[disabled]:pointer-events-none data-[disabled]:opacity-50";

const PRESS_DIM =
  "transition-opacity duration-fast ease-smooth group-data-[state=unchecked]/segment:group-active/segment:opacity-60";

// the checked item's count badge: a tint of the label on the thumb (a tone-soft badge would vanish into the fill)
const ON_THUMB_BADGE =
  "[&_[data-slot=badge]]:text-current [&_[data-slot=badge]]:[--badge-fill:color-mix(in_oklab,currentColor_14%,transparent)]";

// re-renders only when the number changes, so a value change never replays its layout animation
const CountBadge = memo(function CountBadge({ count }: { count: number }) {
  return <Badge size="sm" count={count} />;
});

// ---------------------------------------------------------------- thumb

type Box = { x: number; w: number; h: number };
type ThumbMotion = {
  x: MotionValue<number>;
  scaleX: MotionValue<number>;
  scaleY: MotionValue<number>;
  // stores the box the thumb rests in and returns the one stored before
  rest: (box: Box) => Box | null;
};

// the thumb's box inside the track from layout offsets: the press dip and a running slide do not count
function measure(el: HTMLElement): Box {
  const item = el.offsetParent as HTMLElement | null;
  return { x: el.offsetLeft + (item?.offsetLeft ?? 0), w: el.offsetWidth, h: el.offsetHeight };
}

// the raised thumb lives in the checked item, so css sizes it. when the value moves it starts from the old item's
// box as a transform and springs home (flip), which keeps it on the compositor; the corners are counter-scaled so
// they never stretch between items of different widths
function Thumb({
  motionValues,
  radius,
  pressed,
  reduced,
  className,
}: {
  motionValues: ThumbMotion;
  radius: number;
  pressed: boolean;
  reduced: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const { x, scaleX, scaleY } = motionValues;
  const borderRadius = useTransform([scaleX, scaleY], ([sx, sy]: number[]) =>
    radius ? `${radius / (sx || 1)}px / ${radius / (sy || 1)}px` : "0px",
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = measure(el);
    const prev = motionValues.rest(next);
    if (!prev || reduced || !next.w || !prev.w) {
      x.jump(0);
      scaleX.jump(1);
      scaleY.jump(1);
    } else {
      // where the old thumb is on screen right now, a running slide included, keeping its speed
      const sx = scaleX.get();
      const fromX = prev.x + x.get() - next.x;
      const fromScale = (prev.w * sx) / next.w;
      const vx = x.getVelocity();
      const vs = (scaleX.getVelocity() * prev.w) / next.w;
      x.jump(fromX);
      scaleX.jump(fromScale);
      scaleY.jump(1);
      animateValue(x, 0, { ...springs.snappy, velocity: vx });
      animateValue(scaleX, 1, { ...springs.snappy, velocity: vs });
    }
    // remember where this one rests when it leaves, so the next one starts from the latest layout
    return () => {
      if (el.isConnected) motionValues.rest(measure(el));
    };
  }, [motionValues, reduced, x, scaleX, scaleY]);

  return (
    <motion.span
      ref={ref}
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{ x, scaleX, scaleY, originX: 0, originY: 0 }}
    >
      <motion.span
        data-slot="segmented-control-thumb"
        className={cn("absolute inset-0", className)}
        style={{ borderRadius }}
        initial={false}
        animate={{ scale: pressed && !reduced ? 0.96 : 1 }}
        transition={springs.snappy}
      />
    </motion.span>
  );
}

// ---------------------------------------------------------------- root

export type SegmentedControlProps = Omit<
  ComponentProps<typeof RadioGroupPrimitive.Root>,
  "color" | "value" | "defaultValue" | "onValueChange" | "orientation" | "asChild" | "children"
> & {
  // 2 to 5 options work best; more still fit, with truncated labels (use tabs then)
  options: SegmentedControlOption[];
  value?: string;
  // the first enabled option when left out
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  // track height 32 / 40 / 48px (44px md on touch)
  size?: SegmentedControlSize;
  radius?: SegmentedControlRadius;
  // the thumb's color: neutral is a raised white (dark: lifted gray) surface, other tones fill it
  tone?: Tone;
  // any css color for the thumb; replaces the tone, the label on it turns black or white
  color?: string;
  // items share the full width (the usual phone layout)
  fullWidth?: boolean;
  // icons only; labels become aria-labels and tooltips
  iconOnly?: boolean;
  // hairlines between unselected neighbours, hidden next to the thumb (ios)
  dividers?: boolean;
  // press the thumb and slide across to change the value
  draggable?: boolean;
  classNames?: Partial<Record<SegmentedControlSlot, string>>;
};

// writes an object ref outside render (a hook's return value cannot be mutated in place)
function setObjectRef<T>(ref: { current: T | null } | null | undefined, value: T | null) {
  if (ref) ref.current = value;
}

function isVisible(node: ReactNode) {
  return node != null && node !== false && node !== "";
}

function SegmentedControlRender(segmentedControlProps: SegmentedControlProps) {
  const {
    options,
    value: valueProp,
    defaultValue,
    onValueChange,
    size = "md",
    radius = "md",
    tone = "neutral",
    color,
    fullWidth = false,
    iconOnly = false,
    dividers = true,
    draggable = true,
    disabled = false,
    loop = true,
    classNames,
    className,
    style,
    onKeyDown,
    onFocus,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onClickCapture,
    ref,
    ...props
  } = useDefaults("segmented-control", segmentedControlProps);
  const reduced = usePrefersReducedMotion();
  const firstEnabled = options.find((option) => !option.disabled)?.value ?? "";
  // an empty or stale default (not in options, or disabled) starts on the first enabled option
  const startsOn = options.some((option) => option.value === defaultValue && !option.disabled)
    ? (defaultValue as string)
    : firstEnabled;
  const [value, setValue] = useControllableState({
    value: valueProp,
    defaultValue: startsOn,
    onChange: onValueChange,
  });
  const checkedIndex = options.findIndex((option) => option.value === value);

  // the option picked in this event, so two paths picking it (radix and our focus / key handlers) notify once.
  // cleared after the event: a controlled parent that keeps its value must hear the same pick again next time
  const chosen = useRef<string | null>(null);
  const select = (next: string) => {
    if (next === chosen.current) return;
    chosen.current = next;
    queueMicrotask(() => {
      chosen.current = null;
    });
    setValue(next);
  };
  const neutral = !color && tone === "neutral";
  const scale = TRACK_SIZES[size] ? size : "md";
  const shape = RADII[radius] ? radius : "md";

  const x = useMotionValue(0);
  const scaleX = useMotionValue(1);
  const scaleY = useMotionValue(1);
  const last = useRef<Box | null>(null);
  const thumbMotion = useMemo<ThumbMotion>(
    () => ({
      x,
      scaleX,
      scaleY,
      rest: (box) => {
        const prev = last.current;
        last.current = box;
        return prev;
      },
    }),
    [x, scaleX, scaleY],
  );

  // ---------------------------------------------------------------- drag the thumb

  const track = useRef<HTMLDivElement | null>(null);
  const [pressed, setPressed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ id: number; x: number; y: number; active: boolean; rects: DOMRect[]; index: number } | null>(
    null,
  );
  const swallowClick = useRef(false);

  const items = () =>
    Array.from(track.current?.querySelectorAll<HTMLElement>('[data-slot="segmented-control-item"]') ?? []);

  const endPress = () => {
    drag.current = null;
    setPressed(false);
    setDragging(false);
  };

  // a press that ends outside the track (a mouse leaving it before a drag starts) still lets go of the thumb
  useEffect(() => {
    if (!pressed) return;
    const release = () => {
      if (drag.current?.active) return;
      drag.current = null;
      setPressed(false);
    };
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    return () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
    };
  }, [pressed]);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    onPointerDown?.(event);
    arrowNav.current = false;
    if (event.defaultPrevented || disabled || event.button !== 0) return;
    const item = (event.target as HTMLElement).closest<HTMLElement>('[data-slot="segmented-control-item"]');
    if (!item || item.dataset.state !== "checked") return;
    setPressed(true);
    if (!draggable) return;
    const all = items();
    drag.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      active: false,
      rects: all.map((el) => el.getBoundingClientRect()),
      index: all.indexOf(item),
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    onPointerMove?.(event);
    const state = drag.current;
    if (!state || state.id !== event.pointerId) return;
    if (!state.active) {
      const dx = Math.abs(event.clientX - state.x);
      const dy = Math.abs(event.clientY - state.y);
      // a vertical move first is a page scroll (touch-action: pan-y hands it to the browser)
      if (dy > gestures.slop && dy > dx) {
        endPress();
        return;
      }
      if (dx <= gestures.slop) return;
      state.active = true;
      setDragging(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    // the item under the finger, clamped to the ends; the value changes only when the index does
    const { rects } = state;
    let index = rects.findIndex((rect) => event.clientX >= rect.left && event.clientX < rect.right);
    if (index === -1) {
      const first = rects[0];
      const lastRect = rects[rects.length - 1];
      if (!first || !lastRect) return;
      const rtl = first.left > lastRect.left;
      const beforeStart = rtl ? event.clientX >= first.right : event.clientX < first.left;
      index = beforeStart ? 0 : rects.length - 1;
    }
    if (index === state.index) return;
    const target = items()[index];
    if (!target || target.dataset.disabled !== undefined) return;
    state.index = index;
    const next = target.dataset.value;
    if (next != null) select(next);
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    onPointerUp?.(event);
    const state = drag.current;
    if (state && state.id !== event.pointerId) return;
    // the click after a drag lands on the item the drag began on; it must not undo the move
    if (state?.active) swallowClick.current = true;
    endPress();
  };

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    onClickCapture?.(event);
    if (!swallowClick.current) return;
    swallowClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  // radix selects on an arrow only while the key is still down when its deferred focus lands, so a quick tap
  // (a screen reader, a remote, automation) moved focus without selecting. the focus that follows an arrow selects here
  const arrowNav = useRef(false);
  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    onFocus?.(event);
    if (!arrowNav.current) return;
    arrowNav.current = false;
    const item = (event.target as HTMLElement).closest<HTMLElement>('[data-slot="segmented-control-item"]');
    if (item?.dataset.value != null && item.dataset.disabled === undefined) select(item.dataset.value);
  };

  // arrows move and select (radix); Home and End jump to the first and last enabled option
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    arrowNav.current = event.key.startsWith("Arrow");
    if (event.key !== "Home" && event.key !== "End") return;
    const enabled = items().filter((el) => el.dataset.disabled === undefined);
    const target = event.key === "Home" ? enabled[0] : enabled[enabled.length - 1];
    if (!target) return;
    event.preventDefault();
    target.focus();
    if (target.dataset.value != null) select(target.dataset.value);
  };

  const hasTooltips = options.some((option) => isVisible(option.tooltip)) || iconOnly;

  const root = (
    <RadioGroupPrimitive.Root
      ref={(el: HTMLDivElement | null) => {
        track.current = el;
        if (typeof ref === "function") return ref(el);
        if (typeof ref !== "string") setObjectRef(ref as { current: HTMLDivElement | null } | null | undefined, el);
      }}
      value={value}
      onValueChange={select}
      disabled={disabled}
      loop={loop}
      data-slot="segmented-control"
      data-size={scale}
      data-tone={color ? "custom" : tone}
      data-disabled={disabled || undefined}
      data-dragging={dragging || undefined}
      data-draggable={(draggable && !disabled) || undefined}
      className={cn(
        "relative max-w-full items-stretch touch-pan-y",
        fullWidth ? "flex w-full" : "inline-flex",
        toneClasses[tone] ?? toneClasses.neutral,
        TRACK,
        TRACK_SIZES[scale],
        RADII[shape][scale],
        ICON_SIZES[scale],
        className,
      )}
      style={{ ...colorVars(color), ...style }}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={(event) => {
        onPointerCancel?.(event);
        endPress();
      }}
      onClickCapture={handleClickCapture}
      {...props}
    >
      {options.map((option, index) => {
        const checked = index === checkedIndex;
        const nextToThumb = checked || index - 1 === checkedIndex;
        const labelText = typeof option.label === "string" || typeof option.label === "number" ? String(option.label) : "";
        const showLabel = isVisible(option.label) && !(iconOnly && isVisible(option.icon));
        const ariaLabel = showLabel ? undefined : labelText || option.value;
        const tooltip = isVisible(option.tooltip) ? option.tooltip : showLabel ? undefined : option.label;
        const badge = option.badge;

        const item = (
          <RadioGroupPrimitive.Item
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            aria-label={ariaLabel}
            data-slot="segmented-control-item"
            data-value={option.value}
            className={cn(
              ITEM_BASE,
              ITEM_SIZES[scale],
              iconOnly && ICON_ONLY_SIZES[scale],
              INNER_RADII[shape][scale],
              // equal shares, but a long label keeps its width and the others share what is left
              fullWidth ? "flex-1 basis-0 min-w-fit" : "shrink",
              checked && (neutral ? "text-foreground" : "text-[color:var(--tone-foreground)]"),
              // a mouse sees the thumb can be grabbed
              checked && draggable && "pointer-fine:cursor-grab pointer-fine:active:cursor-grabbing",
              classNames?.item,
            )}
          >
            {/* first in the item: the positioned icon, label and badge after it paint above it */}
            {checked && (
              <Thumb
                motionValues={thumbMotion}
                radius={THUMB_RADIUS_PX[shape][scale]}
                pressed={pressed}
                reduced={reduced}
                className={cn(neutral ? THUMB_NEUTRAL : THUMB_TONE, classNames?.thumb)}
              />
            )}
            {dividers && index > 0 && (
              <span
                data-slot="segmented-control-divider"
                aria-hidden
                className={cn(
                  "pointer-events-none absolute start-0 w-px -translate-x-1/2 bg-foreground/10 transition-opacity duration-fast ease-smooth rtl:translate-x-1/2",
                  DIVIDER_INSETS[scale],
                  nextToThumb && "opacity-0",
                  classNames?.divider,
                )}
              />
            )}
            {isVisible(option.icon) && (
              <span
                data-slot="segmented-control-icon"
                aria-hidden
                className={cn("relative inline-flex shrink-0", PRESS_DIM, classNames?.icon)}
              >
                {option.icon}
              </span>
            )}
            {showLabel && (
              <span
                data-slot="segmented-control-label"
                className={cn("relative min-w-0 truncate", PRESS_DIM, classNames?.label)}
              >
                {option.label}
              </span>
            )}
            {isVisible(badge) &&
              (typeof badge === "number" ? (
                // a count badge animates its width with layout; layoutRoot keeps it from flying when the page moves
                <motion.span
                  layout
                  layoutRoot
                  data-slot="segmented-control-badge"
                  className={cn("relative inline-flex shrink-0 empty:hidden", checked && ON_THUMB_BADGE, classNames?.badge)}
                >
                  <CountBadge count={badge} />
                </motion.span>
              ) : (
                <span data-slot="segmented-control-badge" className={cn("relative inline-flex shrink-0", classNames?.badge)}>
                  {badge}
                </span>
              ))}
          </RadioGroupPrimitive.Item>
        );

        return tooltip ? (
          <Tooltip key={option.value} content={tooltip} disabled={dragging}>
            {item}
          </Tooltip>
        ) : (
          item
        );
      })}
    </RadioGroupPrimitive.Root>
  );

  // one group: after the first tooltip opens, the next ones open at once while you move along
  return hasTooltips ? <TooltipGroup>{root}</TooltipGroup> : root;
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const SegmentedControl = withRef(SegmentedControlRender, "SegmentedControl");

export default SegmentedControl;
